"use client";

import React from "react";
import { Calendar, User, Tag, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

declare const wvcClient: any;

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-12 w-3/4 rounded-lg bg-muted"></div>
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded bg-muted"></div>
          <div className="h-6 w-24 rounded bg-muted"></div>
        </div>
        <div className="h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-full rounded bg-muted"></div>
          <div className="h-4 w-5/6 rounded bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="py-20 text-center">
        <h2 className="font-default text-2xl font-semibold text-foreground">
          Article not found
        </h2>
        <p className="mt-2 text-muted-foreground">
          The resource you are looking for is currently unavailable.
        </p>
        <div className="mt-6">
          <Link to="/BooksPage">
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">Return to Library</Button>
          </Link>
        </div>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <header className="mb-10 text-center md:text-left">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
            {post.categories.map((cat, i) => (
              <Link key={cat.id} to={cat.link || "#"} data-index={i}>
                <span className="inline-flex items-center rounded-md bg-accent/10 px-3 py-1 text-xs font-medium text-accent hover:bg-accent/20 transition-colors">
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>
        )}
        
        <h1 className="font-default text-4xl font-bold leading-tight text-foreground md:text-5xl lg:text-6xl tracking-tight">
          {post.title}
        </h1>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground md:justify-start">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback><User className="size-4" /></AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}
          
          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-lg shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-video w-full object-cover"
          />
        </figure>
      )}

      {}
      <div 
        className="font-default text-lg leading-relaxed text-foreground/90 space-y-6 [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-primary [&>h3]:font-default [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:mt-8 [&>h3]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>blockquote]:border-l-4 [&>blockquote]:border-accent [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>img]:rounded-lg [&>img]:my-8 [&>img]:shadow-md"
        dangerouslySetInnerHTML={{ __html: post.content || post.excerpt || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 flex flex-wrap items-center gap-2">
          <Tag className="size-5 text-muted-foreground" />
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"} data-index={i}>
              <span className="inline-flex items-center rounded-md bg-muted px-2.5 py-1 text-sm font-medium text-muted-foreground hover:bg-muted/80 transition-colors">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      <Separator className="my-12 bg-border" />

      {}
      {post.author && (
        <div className="flex flex-col items-center gap-6 rounded-lg bg-card p-8 text-center md:flex-row md:text-left shadow-sm border border-border/50">
          <Avatar className="size-24 border-4 border-background shadow-md">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="text-2xl"><User className="size-10" /></AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-default text-xl font-bold text-foreground">
              About {post.author.name}
            </h3>
            <p className="mt-2 font-default text-muted-foreground">
              Dustin Royer is an author and speaker dedicated to creating Bible studies and faith-based resources that help readers build a life rooted in purpose and Christian values.
            </p>
            {post.author.link && (
              <div className="mt-4">
                <Link to="/TheAuthorPage">
                  <Button variant="outline" size="sm" className="rounded-md border-primary text-primary hover:bg-primary/5">Meet the Author</Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i} data-index={i} className="overflow-hidden border-none shadow-md">
            <div className="h-48 w-full animate-pulse bg-muted" />
            <CardHeader>
              <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="h-4 w-full animate-pulse rounded bg-muted" />
              <div className="mt-2 h-4 w-2/3 animate-pulse rounded bg-muted" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex flex-col overflow-hidden border-border/50 bg-card transition-all hover:shadow-xl rounded-lg">
          {post.featuredImage && (
            <Link to={post.link || "#"} className="block overflow-hidden">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </Link>
          )}
          <CardHeader className="flex-1">
            {post.categories && post.categories.length > 0 && (
              <span className="mb-2 text-xs font-medium text-accent uppercase tracking-wider">
                {post.categories[0].name}
              </span>
            )}
            <CardTitle className="font-default text-xl leading-tight text-foreground">
              <Link to={post.link || "#"} className="hover:text-accent transition-colors">
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group/btn text-primary hover:bg-accent/5">
                Read Resource
                <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && wvcClient ? wvcClient.getCurrentWPQueryParams() : { post_type: "post" };
  const mainPostQuery = new WP_Query(currentWPQueryParams);

  
  const relatedPostsQuery = new WP_Query({
    post_type: currentWPQueryParams.post_type || "post",
    per_page: 3,
    
  });

  return (
    <section data-section-id="235165" id="single-post-template-section" className="bg-background py-16 md:py-24 text-foreground">
      <div className="container mx-auto px-4">
        
        {}
        <PostsProvider wp_query={mainPostQuery}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mx-auto mt-24 max-w-6xl">
          <div className="mb-10 flex items-center justify-between">
            <h2 className="font-default text-3xl font-bold text-foreground">
              Further Reading
            </h2>
            <Link to="/ResourcesPage">
              <Button variant="outline" className="hidden md:flex rounded-md border-primary text-primary hover:bg-primary/5">
                View All Resources
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedPostsQuery}>
            <RelatedPostsGrid />
          </PostsProvider>
          
          <div className="mt-8 flex justify-center md:hidden">
            <Link to="/ResourcesPage">
              <Button variant="outline" className="rounded-md border-primary text-primary">View All Resources</Button>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}